import {
  ConfirmationOptions,
} from '@NitrexGroup/primevue/confirmationoptions';
import {
  useConfirm,
} from '@NitrexGroup/primevue/useconfirm';
import {
  SEMANTIC_BUTTON_VARIANT_MAP,
  TSemanticButtonVariant,
  semanticButtonVariantAbort,
  semanticButtonVariantDanger,
  semanticButtonVariantStandalone,
} from '@qmulus/ui/components/button/semantic-button.ts';
import {
  shallowRef,
} from 'vue';

import useLibraryI18n from '../i18n/composable.ts';
import {
  isNullish,
} from '../utils/common.ts';

type TCancelButtonVariants = typeof semanticButtonVariantAbort[number];
type TStandaloneButtonVariants = typeof semanticButtonVariantStandalone[number];
type TDangerButtonVariants = typeof semanticButtonVariantDanger[number];

type TPossiblyDynamic<T> = T | (() => T);

export type TConfirmVariant = Exclude<TSemanticButtonVariant, TCancelButtonVariants | TStandaloneButtonVariants>;
export type TUseConfirmArguments = {
  variant: TConfirmVariant,
  action: () => Promise<any>,
  message: TPossiblyDynamic<string>,
  reject?: () => Promise<any>,
  title?: TPossiblyDynamic<string>,
  acceptLabel?: string,
  rejectLabel?: string,
};

export function useSemanticConfirm(confirmArgs: TUseConfirmArguments) {
  const {
    variant,
    action,
    message,
    reject,
    title,
    acceptLabel,
    rejectLabel,
  } = confirmArgs;

  const isActionExecuting = shallowRef(false);
  const confirm = useConfirm();

  const {
    t,
  } = useLibraryI18n('components/button/semantic-button');

  function onReject() {
    if (isNullish(reject)) {
      isActionExecuting.value = false;
    } else {
      reject().finally(() => {
        isActionExecuting.value = false;
      });
    }
  }

  const staticConfirmProps: ConfirmationOptions = {
    // Closing the dialog via the "x" icon, the mask, or Escape bypasses `reject`
    // entirely and only triggers `onHide`, so it must be treated as a cancellation too.
    onHide: onReject,
    rejectLabel: rejectLabel ?? t('cancel'),
    rejectClass: 'p-button-secondary',
    reject: onReject,
    acceptLabel: acceptLabel ?? t(variant),
    acceptIcon: SEMANTIC_BUTTON_VARIANT_MAP[variant].icon,
    acceptClass: semanticButtonVariantDanger.includes(variant as TDangerButtonVariants) ? `p-button-danger` : 'p-button-primary',
    accept: () => {
      isActionExecuting.value = true;
      action().finally(() => {
        isActionExecuting.value = false;
      });
    },
  };

  function doAction() {
    isActionExecuting.value = true;

    let header: string;
    if (isNullish(title)) {
      header = t(variant);
    } else if (typeof title === 'string') {
      header = title;
    } else {
      header = title();
    }

    confirm.require({
      ...staticConfirmProps,
      header,
      message: typeof message === 'string' ? message : message(),
    });
  }

  return {
    doAction,
    isActionExecuting,
  };
}
